import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { IDropdownButtonProps } from "@similarweb/ui-components";

import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import type { AppsSdksSignalSelection } from "app/pages/sales-intelligence/domain/signals/types/apps-signal";

import useSignalFilterDropdownLabel from "../../../hooks/useSignalFilterDropdownLabel";
import type { TranslatableNameWithType } from "../../../models/services/FilterNamingService";
import { buildSdksSelectionName } from "../../../services/filters/sdks/sdksSelectionNameBuilder";
import signalsI18nKeys from "../../../utils/i18n";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "../shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import SignalFilterDropdownLabel from "../shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";

import FilterSdksDropdown from "./FilterSdksDropdown/FilterSdksDropdown";
import useSdksFilterState from "./hooks/useSdksFilterState";

type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen">;

export type SignalSdksFilterProps = {
    filterKey: "sdks";
    isValueEmpty: boolean;
    isDisabled?: boolean;
    selectedValue: AppsSdksSignalSelection;
    options: readonly IAppsSdkDescriptionItemDto[];
    onValueChange(value: AppsSdksSignalSelection): void;
    onNoResultsFound?: VoidFunction;
};

const SignalSdksFilter = (props: SignalSdksFilterProps) => {
    const { filterKey, isValueEmpty, isDisabled, selectedValue, options, onValueChange, onNoResultsFound } = props;

    const { translate } = useTranslate();

    const { onItemClick } = useSdksFilterState(selectedValue, onValueChange);

    const buildFilterName = useCallback((): Readonly<TranslatableNameWithType> => {
        return buildSdksSelectionName(selectedValue);
    }, [selectedValue]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const renderButton = ({ isOpen }: DropdownButtonProps) => {
        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                disabled={isDisabled}
                hasValue={!isValueEmpty}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    return (
        <FilterSdksDropdown
            options={options}
            selectedValue={selectedValue}
            isDisabled={isDisabled}
            buttonComponent={renderButton}
            searchPlaceholder={translate(signalsI18nKeys.apps.sdksFilter.searchPlaceholder)}
            onNoResultsFound={onNoResultsFound}
            onSelect={onItemClick}
        />
    );
};

export default SignalSdksFilter;
